import * as React from "react";

export type Theme = "dark" | "light" | "system";
export type ResolvedTheme = "dark" | "light";

type ThemeProviderProps = {
	children: React.ReactNode;
	defaultTheme?: Theme;
	storageKey?: string;
	disableTransitionOnChange?: boolean;
};

type ThemeProviderState = {
	theme: Theme;
	resolvedTheme: ResolvedTheme;
	setTheme: (theme: Theme) => void;
};

const COLOR_SCHEME_QUERY = "(prefers-color-scheme: dark)";
const THEME_VALUES: Theme[] = ["dark", "light", "system"];

const ThemeProviderContext = React.createContext<
	ThemeProviderState | undefined
>(undefined);

function isTheme(value: string | null): value is Theme {
	if (value === null) {
		return false;
	}
	return THEME_VALUES.includes(value as Theme);
}

function getSystemTheme(): ResolvedTheme {
	if (typeof window === "undefined") return "light";
	if (window.matchMedia(COLOR_SCHEME_QUERY).matches) {
		return "dark";
	}
	return "light";
}

function disableTransitionsTemporarily() {
	if (typeof document === "undefined") return () => {};
	const style = document.createElement("style");
	style.appendChild(
		document.createTextNode(
			"*,*::before,*::after{-webkit-transition:none!important;transition:none!important}",
		),
	);
	document.head.appendChild(style);

	return () => {
		window.getComputedStyle(document.body);
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				style.remove();
			});
		});
	};
}

export function ThemeProvider({
	children,
	defaultTheme = "system",
	storageKey = "theme",
	disableTransitionOnChange = true,
	...props
}: ThemeProviderProps) {
	const [theme, setThemeState] = React.useState<Theme>(() => {
		if (typeof window !== "undefined") {
			const storedTheme = localStorage.getItem(storageKey);
			if (isTheme(storedTheme)) {
				return storedTheme;
			}
		}
		return defaultTheme;
	});

	const [resolvedTheme, setResolvedTheme] = React.useState<ResolvedTheme>(
		() => {
			if (theme === "system") {
				return getSystemTheme();
			}
			return theme as ResolvedTheme;
		},
	);

	const setTheme = React.useCallback(
		(nextTheme: Theme) => {
			localStorage.setItem(storageKey, nextTheme);
			setThemeState(nextTheme);
		},
		[storageKey],
	);

	const applyTheme = React.useCallback(
		(nextTheme: Theme) => {
			const root = document.documentElement;
			const resolved = nextTheme === "system" ? getSystemTheme() : nextTheme;
			const restoreTransitions = disableTransitionOnChange
				? disableTransitionsTemporarily()
				: null;

			root.classList.remove("light", "dark");
			root.classList.add(resolved);
			setResolvedTheme(resolved as ResolvedTheme);

			if (restoreTransitions) {
				restoreTransitions();
			}
		},
		[disableTransitionOnChange],
	);

	React.useEffect(() => {
		applyTheme(theme);

		if (theme !== "system") {
			return undefined;
		}

		const mediaQuery = window.matchMedia(COLOR_SCHEME_QUERY);
		const handleChange = () => {
			applyTheme("system");
		};

		mediaQuery.addEventListener("change", handleChange);

		return () => {
			mediaQuery.removeEventListener("change", handleChange);
		};
	}, [theme, applyTheme]);

	React.useEffect(() => {
		const handleStorageChange = (event: StorageEvent) => {
			if (event.storageArea !== localStorage) {
				return;
			}

			if (event.key !== storageKey) {
				return;
			}

			if (isTheme(event.newValue)) {
				setThemeState(event.newValue);
				return;
			}

			setThemeState(defaultTheme);
		};

		window.addEventListener("storage", handleStorageChange);

		return () => {
			window.removeEventListener("storage", handleStorageChange);
		};
	}, [defaultTheme, storageKey]);

	const value = React.useMemo(
		() => ({
			theme,
			resolvedTheme,
			setTheme,
		}),
		[theme, resolvedTheme, setTheme],
	);

	return (
		<ThemeProviderContext.Provider {...props} value={value}>
			{children}
		</ThemeProviderContext.Provider>
	);
}

export const useTheme = () => {
	const context = React.useContext(ThemeProviderContext);

	if (context === undefined) {
		throw new Error("useTheme must be used within a ThemeProvider");
	}

	return context;
};
